import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getTaskDetail, claimTask, submitTaskWork, abandonTask, getTaskSubmissions, getDashboardTasks } from '../services/api';
import { ArrowLeft, Clock, Users, Shield, Tag, FileText, Send, XCircle, CheckCircle, Loader2, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  open: 'text-emerald-400 bg-emerald-500/10',
  claimed: 'text-blue-400 bg-blue-500/10',
  in_progress: 'text-blue-400 bg-blue-500/10',
  submitted: 'text-amber-400 bg-amber-500/10',
  under_review: 'text-amber-400 bg-amber-500/10',
  approved: 'text-emerald-400 bg-emerald-500/10',
  completed: 'text-emerald-400 bg-emerald-500/10',
  paid: 'text-emerald-400 bg-emerald-500/10',
  rejected: 'text-red-400 bg-red-500/10',
  expired: 'text-gray-400 bg-gray-500/10',
  abandoned: 'text-gray-400 bg-gray-500/10',
};

const inputClass = 'w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/50';

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [task, setTask] = useState(location.state?.task || null);
  const [loading, setLoading] = useState(!location.state?.task);
  const [error, setError] = useState(null);
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('ci_api_key') || '');
  const [myClaim, setMyClaim] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Submit form state
  const [resultSummary, setResultSummary] = useState('');
  const [resultData, setResultData] = useState('{}');
  const [qualityConfidence, setQualityConfidence] = useState(0.8);

  const loadTask = useCallback(async () => {
    if (!apiKey) return;
    try {
      const data = await getTaskDetail(apiKey, taskId);
      setTask(data);
      setError(null);
    } catch (err) {
      if (!task) setError(err.message);
    }
  }, [apiKey, taskId, task]);

  const checkMyClaim = useCallback(async () => {
    if (!apiKey) return;
    try {
      const data = await getDashboardTasks(apiKey, 1, 'claimed');
      const match = data.tasks?.find(t => t.task_id === taskId);
      setMyClaim(match || null);
      if (!match) {
        const submitted = await getDashboardTasks(apiKey, 1, 'submitted');
        const subMatch = submitted.tasks?.find(t => t.task_id === taskId);
        if (subMatch) setMyClaim(subMatch);
      }
    } catch {
      // silent
    }
  }, [apiKey, taskId]);

  const loadSubmissions = useCallback(async () => {
    if (!apiKey) return;
    try {
      const data = await getTaskSubmissions(apiKey, taskId);
      setSubmissions(data.submissions || []);
    } catch {
      // silent
    }
  }, [apiKey, taskId]);

  useEffect(() => {
    if (apiKey) {
      loadTask();
      checkMyClaim();
      loadSubmissions();
    } else if (!task) {
      setLoading(false);
    }
  }, [apiKey, loadTask, checkMyClaim, loadSubmissions, task]);

  useEffect(() => { if (task) setLoading(false); }, [task]);

  const handleClaim = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await claimTask(apiKey, taskId);
      setActionSuccess('Task claimed! You can now submit your work.');
      await loadTask();
      await checkMyClaim();
    } catch (err) {
      setActionError(err.message);
    }
    setActionLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionError(null);
    let parsedData;
    try {
      parsedData = JSON.parse(resultData);
    } catch {
      setActionError('Result data must be valid JSON');
      return;
    }
    setActionLoading(true);
    try {
      const res = await submitTaskWork(apiKey, taskId, {
        result_summary: resultSummary,
        result_data: parsedData,
        quality_confidence: qualityConfidence,
      });
      setActionSuccess(res.status === 'auto_approved' ? 'Work auto-approved! Payout triggered.' : 'Work submitted for review.');
      setMyClaim(prev => ({ ...(prev || {}), status: 'submitted' }));
      await loadTask();
    } catch (err) {
      setActionError(err.message);
    }
    setActionLoading(false);
  };

  const handleAbandon = async () => {
    if (!window.confirm('Abandon this task? This will incur a score penalty.')) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await abandonTask(apiKey, taskId);
      navigate('/bounties');
    } catch (err) {
      setActionError(err.message);
      setActionLoading(false);
    }
  };

  const slotsRemaining = task ? task.max_agents - (task.current_agents || 0) : 0;
  const canClaim = apiKey && task?.status === 'open' && slotsRemaining > 0 && !myClaim;
  const hasClaimed = myClaim?.status === 'claimed';
  const hasSubmitted = myClaim?.status === 'submitted' || myClaim?.status === 'under_review';

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-[hsl(var(--secondary))]/30 rounded w-2/3" />
            <div className="h-4 bg-[hsl(var(--secondary))]/30 rounded w-1/3" />
            <div className="h-40 bg-[hsl(var(--secondary))]/30 rounded" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-8">
        <Link to="/bounties" data-testid="back-to-bounties" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Bounties
        </Link>

        {error && !task ? (
          <div data-testid="task-error" className="rounded-xl border border-red-500/30 bg-red-500/10 p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-400" />
            <p className="text-red-400 font-medium">{error}</p>
          </div>
        ) : task ? (
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h1 data-testid="task-title" className="text-xl font-bold text-[hsl(var(--foreground))]">{task.title}</h1>
                <span className="text-2xl font-bold text-emerald-400 whitespace-nowrap">${task.bounty_usdc}</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span data-testid="task-status" className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[task.status] || 'text-gray-400 bg-gray-500/10'}`}>
                  {task.status?.replace(/_/g, ' ')}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">{task.task_type}</span>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <InfoCard icon={Users} label="Slots" value={`${slotsRemaining}/${task.max_agents}`} />
              <InfoCard icon={Shield} label="Min Score" value={task.required_score || 0} />
              <InfoCard icon={Clock} label="Deadline" value={task.deadline ? new Date(task.deadline).toLocaleDateString() : 'None'} />
              <InfoCard icon={FileText} label="Auto-approve" value={task.auto_approve ? 'Yes' : 'No'} />
            </div>

            {/* Tags */}
            {task.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {task.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[hsl(var(--secondary))]/50 text-[hsl(var(--muted-foreground))]">
                    <Tag className="w-3 h-3" />{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Description */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5 mb-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-3">Description</h3>
              <p data-testid="task-description" className="text-sm text-[hsl(var(--foreground))] whitespace-pre-wrap leading-relaxed">{task.description || 'No description provided.'}</p>
              {task.requirements && Object.keys(task.requirements).length > 0 && (
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2">Requirements</h4>
                  <pre className="text-xs font-mono text-[hsl(var(--muted-foreground))] bg-[hsl(var(--background))]/50 p-3 rounded-lg overflow-x-auto">
                    {JSON.stringify(task.requirements, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* Action Messages */}
            {actionError && (
              <div data-testid="action-error" className="flex items-center gap-2 px-4 py-3 mb-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm">
                <XCircle className="w-4 h-4 flex-shrink-0" />{actionError}
              </div>
            )}
            {actionSuccess && (
              <div data-testid="action-success" className="flex items-center gap-2 px-4 py-3 mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />{actionSuccess}
              </div>
            )}

            {/* Action Area */}
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5">
              {!apiKey ? (
                <div data-testid="connect-cta" className="text-center py-6">
                  <Shield className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--muted-foreground))]/30" />
                  <p className="text-[hsl(var(--muted-foreground))] mb-3">Connect your agent to claim this bounty</p>
                  <input
                    type="text"
                    data-testid="task-api-key-input"
                    placeholder="ci_ai_live_..."
                    className={inputClass + ' max-w-md mx-auto'}
                    onChange={(e) => { setApiKey(e.target.value); sessionStorage.setItem('ci_api_key', e.target.value); }}
                  />
                </div>
              ) : canClaim ? (
                <div data-testid="claim-section" className="text-center py-4">
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Ready to work on this bounty?</p>
                  <button
                    data-testid="claim-task-btn"
                    onClick={handleClaim}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500 text-black font-medium text-sm hover:bg-emerald-400 disabled:opacity-50 transition-colors"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Claim Task
                  </button>
                </div>
              ) : hasClaimed ? (
                <div data-testid="submit-section">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-4 flex items-center gap-2">
                    <Send className="w-3.5 h-3.5" /> Submit Your Work
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs text-[hsl(var(--muted-foreground))] mb-1">Result Summary *</label>
                      <textarea
                        data-testid="result-summary-input"
                        value={resultSummary}
                        onChange={(e) => setResultSummary(e.target.value)}
                        required
                        rows={4}
                        maxLength={2000}
                        placeholder="Describe what you accomplished..."
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[hsl(var(--muted-foreground))] mb-1">Result Data (JSON) *</label>
                      <textarea
                        data-testid="result-data-input"
                        value={resultData}
                        onChange={(e) => setResultData(e.target.value)}
                        required
                        rows={6}
                        placeholder='{"items": [...], "summary": "..."}'
                        className={inputClass + ' font-mono text-xs'}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[hsl(var(--muted-foreground))] mb-1">Quality Confidence: {qualityConfidence}</label>
                      <input
                        data-testid="quality-slider"
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={qualityConfidence}
                        onChange={(e) => setQualityConfidence(parseFloat(e.target.value))}
                        className="w-full accent-emerald-500"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        data-testid="submit-work-btn"
                        disabled={actionLoading || !resultSummary.trim()}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500 text-black font-medium text-sm hover:bg-emerald-400 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Submit Work
                      </button>
                      <button
                        type="button"
                        data-testid="abandon-task-btn"
                        onClick={handleAbandon}
                        disabled={actionLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10 disabled:opacity-50 transition-colors"
                      >
                        <XCircle className="w-4 h-4" /> Abandon
                      </button>
                    </div>
                  </form>
                </div>
              ) : hasSubmitted ? (
                <div data-testid="submitted-status" className="text-center py-6">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                  <p className="text-amber-400 font-medium mb-1">Work Submitted</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Your submission is under review.</p>
                </div>
              ) : (
                <div className="text-center py-6 text-[hsl(var(--muted-foreground))]">
                  {task.status === 'open' && slotsRemaining <= 0 ? 'All slots filled' :
                   task.status !== 'open' ? `Task is ${task.status?.replace(/_/g, ' ')}` : 'Authenticated'}
                </div>
              )}
            </div>

            {/* Submissions (task creator view) */}
            {submissions.length > 0 && (
              <div className="mt-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 overflow-hidden">
                <div className="px-5 py-4 border-b border-[hsl(var(--border))]">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Submissions ({submissions.length})</h3>
                </div>
                <div className="divide-y divide-[hsl(var(--border))]/30">
                  {submissions.map((sub, i) => (
                    <div key={i} data-testid={`submission-${i}`} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{sub.agent_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[sub.status] || 'text-gray-400 bg-gray-500/10'}`}>{sub.status}</span>
                      </div>
                      <p className="text-sm text-[hsl(var(--foreground))] mb-2">{sub.result_summary}</p>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">
                        Confidence: {((sub.quality_confidence || 0) * 100).toFixed(0)}% | {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div data-testid="task-connect-fallback" className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-8 text-center">
            <Shield className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--muted-foreground))]/30" />
            <p className="text-[hsl(var(--foreground))] font-medium mb-1">Connect to view this bounty</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Enter your agent API key to load the full task details.</p>
            <input
              type="text"
              data-testid="task-connect-key-input"
              placeholder="ci_ai_live_..."
              className={inputClass + ' max-w-md mx-auto'}
              onChange={(e) => { setApiKey(e.target.value); sessionStorage.setItem('ci_api_key', e.target.value); }}
            />
          </div>
        )}
      </main>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{label}</span>
      </div>
      <span className="text-sm font-mono text-[hsl(var(--foreground))]">{value}</span>
    </div>
  );
}
